import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Tooltip } from '@material-ui/core';
import { getColor } from 'common/constants/_colors';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { editCustomPmMetricsModalOptions } from 'modals/modal-options-factory';
import { useDispatch, useSelector } from 'react-redux';
import { toggleModal } from 'state/callers';
import { allProjectsSelector } from 'state/selectors/projects';
import { teamsSelector } from 'state/selectors/teams';
import styled from 'styled-components';
import { CAN_EDIT_ACCOUNT_TYPES } from '@linearb/linjs-common';
import { CUSTOM_FIELDS_KEY } from 'modals/EditCustomPmMetricsModal/EditCustomPmMetricsModal.constants';
import { getRemValueInPixel } from 'common/utils/utils';
import { TeamToolTip } from 'components/Tooltips/labels-list/TeamTooltip';
import { TeamIconWrapper } from 'components/LabelsList';

const LabelWrapper = styled(Flex)`
  :hover {
    background-color: #f5f7fa;
    cursor: pointer;
  }

  background-color: ${getColor('white')};
  border: solid 1px #d8deea;
  border-radius: 8px;
  height: 24px;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
`;

const ImageWrapper = styled.img`
  height: 1rem;
  width: 1rem;
  margin-right: 0.3rem;
`;

export const LabelsSection = ({
  filters,
  issueTypes,
  widget = {},
  setEditableReport,
  loading = false,
  editableReport,
  setEditMode,
  account,
  onChange
}) => {
  const issueTypesByNames = _.keyBy(issueTypes, 'name');
  const orgTeams = useSelector(teamsSelector);
  const teamsProjects = useSelector(allProjectsSelector);
  const containerRef = useRef(null);
  const [maxLetters, setMaxLetters] = useState(30);

  useEffect(() => {
    if (containerRef?.current?.offsetWidth) {
      const rem = getRemValueInPixel();
      setMaxLetters((containerRef.current.offsetWidth - 4 * rem) / rem);
    }
  }, []);

  const dispatch = useDispatch();

  if (loading) {
    return null;
  }

  const openModal = () =>
    toggleModal(
      editCustomPmMetricsModalOptions({
        widget,
        setEditableReport,
        editableReport,
        issueTypes,
        setEditMode,
        onChange
      })
    )(dispatch);

  const TagLabel = ({
    name,
    color,
    marginTop,
    marginRight,
    marginLeft,
    marginBottom,
    testid
  }) => (
    <LabelWrapper
      items='center'
      mt={marginTop}
      mr={marginRight}
      mb={marginBottom}
      ml={marginLeft}
      onClick={
        CAN_EDIT_ACCOUNT_TYPES.includes(account.type_id) ? openModal : null
      }
      data-testid={testid}
    >
      {_.get(issueTypesByNames[name], 'iconUrl') && (
        <ImageWrapper src={_.get(issueTypesByNames[name], 'iconUrl')} />
      )}
      <Flex items='center'>
        {color && <TeamIconWrapper color={color} />}
        <Text>{name}</Text>
      </Flex>
    </LabelWrapper>
  );

  const teamsByIds = _.keyBy(orgTeams, 'id', []);
  const filtersToLabels = Object.values(_.omit(filters, CUSTOM_FIELDS_KEY))
    .reduce((acc, val) => acc.concat(val), [])
    .reverse()
    .filter(item => item);

  if (_.get(widget, 'created_only')) {
    filtersToLabels.push({ name: 'Created items' });
  }

  let labelsLength = 0;
  let labelsCount = 0;
  let moreLabels = filtersToLabels.map(item => item);

  const MoreLabelsTooltip = ({ labels }) => (
    <Flex
      direction='row'
      pt='x-small'
      pb='x-small'
      maxWidth='40rem'
      wrap='wrap'
    >
      {labels.map(({ title, color, name }) => (
        <Flex key={name || title}>
          {title && (
            <TagLabel
              name={name || title}
              color={color}
              marginTop='x-x-small'
              marginBottom='x-x-small'
              marginLeft='x-x-small'
              marginRight='x-x-small'
            />
          )}
        </Flex>
      ))}
    </Flex>
  );

  return (
    <Flex wrap='wrap' ref={containerRef}>
      {filtersToLabels.map((filter, key) => {
        const filterName = _.get(filter, 'title') || _.get(filter, 'name');
        const filterId = _.get(filter, 'id');
        if (labelsLength < maxLetters) {
          labelsLength += filterName?.length || 0;
          labelsCount++;
          moreLabels = moreLabels.filter(item => item !== filter);
          return (
            <>
              {filter && (
                <Tooltip
                  key={key}
                  display='block'
                  placement='bottom'
                  disableHoverListener={!teamsByIds[filterId]}
                  data-testid={`${filterName}-label-tooltip`}
                  title={
                    <TeamToolTip
                      teamName={filterName}
                      filters={_.get(
                        _.keyBy(teamsProjects, 'name')[filterName],
                        'filter'
                      )}
                      teamsByIds={teamsByIds}
                      id={filterId}
                    />
                  }
                >
                  <div>
                    <TagLabel
                      marginRight={'x-x-small'}
                      marginBottom={'x-x-small'}
                      name={filterName}
                      color={_.get(teamsByIds[filterId], 'color')}
                      testid={`${filterName}-label`}
                    />
                  </div>
                </Tooltip>
              )}
            </>
          );
        }
      })}
      {filtersToLabels.length > labelsCount && (
        <Tooltip title={<MoreLabelsTooltip labels={moreLabels} />}>
          <div>
            <TagLabel
              name={
                <Text color={'dodgerBlue'}>
                  + {filtersToLabels.length - labelsCount}
                </Text>
              }
              testid='more-labels-label'
            />
          </div>
        </Tooltip>
      )}
    </Flex>
  );
};
